import React from 'react';
import { Contact } from '../../data/go2LegendeMock';
import { Trophy, Coins, TrendingUp } from 'lucide-react';

interface Props {
  user: Contact;
}

const STATUS_MAP: Record<string, string> = {
  'Traveler': 'Putnik',
  'World Traveler': 'Svetski Putnik',
  'Legend': 'Legenda'
};

export const DashboardSummary: React.FC<Props> = ({ user }) => {
  const nextLevel = user.loyalty_status === 'Traveler' ? 'Svetski Putnik' : user.loyalty_status === 'World Traveler' ? 'Legenda' : null;
  const nextLevelThreshold = user.loyalty_status === 'Traveler' ? 800 : 1500;
  const spendNeeded = nextLevel ? Math.max(0, nextLevelThreshold - user.loyalty_total_spend_last_24m) : 0;
  const progressPercent = nextLevel ? Math.min(100, (user.loyalty_total_spend_last_24m / nextLevelThreshold) * 100) : 100;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {/* Level Card */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Tvoj Nivo</h3>
          <Trophy className={`w-6 h-6 ${user.loyalty_status === 'Legend' ? 'text-purple-500' : user.loyalty_status === 'World Traveler' ? 'text-brand-500' : 'text-gray-400'}`} />
        </div>
        <div className="text-2xl font-bold text-go2-pink mb-2">{STATUS_MAP[user.loyalty_status] || user.loyalty_status}</div>
        
        {nextLevel ? (
          <div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div className="bg-brand-600 h-2.5 rounded-full" style={{ width: `${progressPercent}%` }}></div>
            </div>
            <p className="text-sm text-gray-600">
              Potroši još <span className="font-bold">{spendNeeded} EUR</span> da dostigneš {nextLevel}.
            </p>
          </div>
        ) : (
          <p className="text-sm text-purple-600 font-medium">Dostigao si najviši nivo!</p>
        )}
      </div>

      {/* Points Card */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Tvoji Bodovi</h3>
          <Coins className="w-6 h-6 text-yellow-500" />
        </div>
        <div className="text-2xl font-bold text-go2-pink mb-2">{user.loyalty_points_balance}</div>
        <p className="text-sm text-gray-600">
          Vredi oko <span className="font-bold text-green-600">{(user.loyalty_points_balance / 20).toFixed(2)} EUR</span> popusta
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Iskoristi za do {user.loyalty_status === 'Legend' ? '40%' : user.loyalty_status === 'World Traveler' ? '30%' : '20%'} cene putovanja
        </p>
      </div>

      {/* Stats Card */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Ukupna Statistika</h3>
          <TrendingUp className="w-6 h-6 text-green-500" />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Ukupno zarađeno:</span>
            <span className="font-medium text-go2-pink">{user.loyalty_points_lifetime_earned} pts</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Ukupno iskorišćeno:</span>
            <span className="font-medium text-go2-pink">{user.loyalty_points_lifetime_redeemed} pts</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Potrošnja (24m):</span>
            <span className="font-medium text-go2-pink">{user.loyalty_total_spend_last_24m} EUR</span>
          </div>
        </div>
      </div>
    </div>
  );
};
